import React, { useEffect, useState } from 'react';
import { Save, ShieldCheck, Loader2, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import {
  DEFAULT_LEAD_LOCK,
  DEFAULT_MEDIA,
  LOCKABLE_FIELDS,
  LeadLockSettings,
  MediaSettings,
  loadListingSettings,
  saveListingSettings,
} from '@/lib/listingSettings';


/**
 * Admin → Settings.
 * Writes the shared mkt_settings rows that every uploader / job card reads.
 * The table's RLS policies only allow an is_admin profile to write.
 */
const AdminListingSettings: React.FC = () => {
  const { t } = useI18n();
  const [media, setMedia] = useState<MediaSettings>(DEFAULT_MEDIA);
  const [lock, setLock] = useState<LeadLockSettings>(DEFAULT_LEAD_LOCK);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    loadListingSettings(true).then((s) => {
      setMedia(s.media);
      setLock(s.lock);
      setLoading(false);
    });
  }, []);

  const num = (v: string, fallback: number) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };

  const toggleField = (field: string) =>
    setLock((l) => ({
      ...l,
      hidden_fields: l.hidden_fields.includes(field)
        ? l.hidden_fields.filter((f) => f !== field)
        : [...l.hidden_fields, field],
    }));

  const save = async () => {
    setBusy(true);
    setMsg('');
    setErr('');
    const error = await saveListingSettings(media, lock);
    if (!error) {
      // Changing what the public feed exposes is a security-relevant action, so
      // it is written to the audit log (admin-read-only) with no sensitive data.
      supabase
        .from('mkt_security_events')
        .insert({
          event_type: 'admin_setting_changed',
          severity: lock.enabled ? 'info' : 'warning',
          outcome: 'saved',
          detail: `listing media/lead-lock updated (lock=${lock.enabled ? 'on' : 'OFF'}, hidden=${lock.hidden_fields.join('|') || 'none'}, images=${media.max_images}, pdfs=${media.max_documents})`,
        })
        .then(() => undefined);
    }
    setBusy(false);
    if (error) setErr(t('set.saveErr'));
    else setMsg(t('set.saved'));
  };


  const inputCls =
    'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-semibold text-slate-900 focus:border-slate-900 focus:outline-none';
  const labelCls = 'mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500';

  if (loading) return <p className="text-sm text-slate-500">{t('common.loading')}</p>;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <h2 className="text-lg font-extrabold text-slate-900">{t('set.title')}</h2>
      <p className="mt-1 text-sm text-slate-500">{t('set.sub')}</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className={labelCls}>{t('set.maxImages')}</label>
          <input
            type="number"
            min={1}
            max={60}
            className={inputCls}
            value={media.max_images}
            onChange={(e) => setMedia({ ...media, max_images: num(e.target.value, DEFAULT_MEDIA.max_images) })}
          />
        </div>
        <div>
          <label className={labelCls}>{t('set.maxDocs')}</label>
          <input
            type="number"
            min={1}
            max={40}
            className={inputCls}
            value={media.max_documents}
            onChange={(e) => setMedia({ ...media, max_documents: num(e.target.value, DEFAULT_MEDIA.max_documents) })}
          />
        </div>
        <div>
          <label className={labelCls}>{t('set.maxImageMb')}</label>
          <input
            type="number"
            min={1}
            max={50}
            className={inputCls}
            value={media.max_image_mb}
            onChange={(e) => setMedia({ ...media, max_image_mb: num(e.target.value, DEFAULT_MEDIA.max_image_mb) })}
          />
        </div>
        <div>
          <label className={labelCls}>{t('set.maxDocMb')}</label>
          <input
            type="number"
            min={1}
            max={100}
            className={inputCls}
            value={media.max_document_mb}
            onChange={(e) => setMedia({ ...media, max_document_mb: num(e.target.value, DEFAULT_MEDIA.max_document_mb) })}
          />
        </div>
      </div>

      <div className="mt-5">
        <p className={labelCls}>{t('set.fileTypes')}</p>
        <div className="flex flex-wrap gap-2">
          {[...media.image_types, ...media.document_types].map((mt) => (
            <span
              key={mt}
              className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700"
            >
              {mt}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-6 border-t border-slate-100 pt-5">
        <label className="flex items-start gap-2.5">
          <input
            type="checkbox"
            checked={lock.enabled}
            onChange={(e) => setLock({ ...lock, enabled: e.target.checked })}
            className="mt-1 h-4 w-4"
          />
          <span className="text-sm font-bold text-slate-800">{t('set.lockEnabled')}</span>
        </label>

        {!lock.enabled && (
          <p className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {t('set.lockOffWarn')}
          </p>
        )}


        <p className={`${labelCls} mt-4`}>{t('set.lockFields')}</p>
        <div className="flex flex-wrap gap-2">
          {LOCKABLE_FIELDS.map((field) => {
            const on = lock.hidden_fields.includes(field);
            return (
              <button
                key={field}
                type="button"
                onClick={() => toggleField(field)}
                className={`rounded-lg border px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
                  on ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-600'
                }`}
              >
                {field}
              </button>
            );
          })}
        </div>

        <p className="mt-4 flex items-start gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-xs font-semibold text-emerald-800">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          {t('set.dbNote')}
        </p>
      </div>

      {msg && <p className="mt-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">{msg}</p>}
      {err && <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{err}</p>}

      <button
        onClick={save}
        disabled={busy}
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-700 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {busy ? t('common.saving') : t('common.save')}
      </button>
    </div>
  );
};

export default AdminListingSettings;
